import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toast } from "sonner";

import { isSessionUnavailable, SESSION_UNAVAILABLE_MESSAGE } from "@/lib/axios";
import { formatError } from "@/lib/error";

// errorMessage: false opts a query out of the global error toast
declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { errorMessage?: string | false };
    mutationMeta: { errorMessage?: string | false };
  }
}

function is4xx(error: Error): boolean {
  return (
    isAxiosError(error) &&
    error.response != null &&
    error.response.status >= 400 &&
    error.response.status < 500
  );
}

function is401(error: Error): boolean {
  return isAxiosError(error) && error.response?.status === 401;
}

// A request whose session refresh got no verdict (429, 5xx, timeout) is
// retried a few times, after the refresh's Retry-After when it sent one
// (T-245). Ordinary failures keep the single retry.
const SESSION_UNAVAILABLE_RETRIES = 3;
const MAX_RETRY_DELAY_MS = 60_000;

function retryDelay(failureCount: number, error: Error): number {
  if (isSessionUnavailable(error) && error.retryAfterMs != null) {
    return Math.min(error.retryAfterMs, MAX_RETRY_DELAY_MS);
  }
  return Math.min(1000 * 2 ** failureCount, 30_000);
}

// One toast however many requests fail in the same window: sonner replaces a
// toast that shares an id instead of stacking a new one.
const SESSION_UNAVAILABLE_TOAST_ID = "session-unavailable";

function toastSessionUnavailable(message: string) {
  toast.error(message, { id: SESSION_UNAVAILABLE_TOAST_ID });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error) =>
        isSessionUnavailable(error)
          ? failureCount < SESSION_UNAVAILABLE_RETRIES
          : !is4xx(error) && failureCount < 1,
      retryDelay,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // A 401 that reaches here means the refresh ended the session, and the
      // axios interceptor is already on its way to /logout.
      if (is401(error)) return;
      const message = query.meta?.errorMessage;
      if (message === false) return;
      if (isSessionUnavailable(error)) {
        toastSessionUnavailable(
          "Couldn't reach the server. Try again in a moment.",
        );
        return;
      }
      // A stable id per query collapses repeats: a query that fails across
      // several mounts shows one toast, not a stack of identical ones.
      toast.error(formatError(error, message || undefined), {
        id: `query-error:${JSON.stringify(query.queryKey)}`,
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (is401(error)) return;
      const message = mutation.meta?.errorMessage;
      // A change lost to an unreachable server must not fail silently. A
      // mutation that reports through meta gets the shared toast instead of
      // its own wording; forms that report errors themselves show the same
      // message through formatError, so they aren't toasted twice.
      if (isSessionUnavailable(error)) {
        if (message) toastSessionUnavailable(SESSION_UNAVAILABLE_MESSAGE);
        return;
      }
      // mutations toast only when opted in via meta — form mutations surface
      // errors through applyServerErrors in the component instead
      if (message) toast.error(formatError(error, message));
    },
  }),
});
